import { CommonModule } from '@angular/common';
import { Component, Inject, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CustomCellComponent } from '@vendasta/galaxy/table';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { CrmObjectInterface } from '@vendasta/crm';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { switchWhen } from '@vendasta/rx-utils';
import { CrmAssociationService, CrmObjectDisplayService } from '../../../shared-services';
import { PAGE_ROUTES } from '../../../constants';

interface cellData {
  objectType: ObjectType;
  objectId: string;
  showUrl: boolean;
}

interface primaryAssociation {
  crmObject: CrmObjectInterface;
  objectType: ObjectType;
}

@Component({
  styleUrls: ['../shared.scss'],
  templateUrl: './primary-association-cell.component.html',
  imports: [RouterLink, CommonModule],
})
export class PrimaryAssociationCellComponent extends CustomCellComponent {
  private readonly objectId$$ = new ReplaySubject<string>(1);
  private readonly objectType$$ = new ReplaySubject<ObjectType>(1);
  private readonly showUrl$$ = new BehaviorSubject<boolean>(false);

  @Input()
  set value(cellData: cellData) {
    this.objectType$$.next(cellData.objectType);
    this.objectId$$.next(cellData.objectId);
    this.showUrl$$.next(cellData.showUrl);
  }

  private readonly primaryAssociation$ = combineLatest([this.objectType$$, this.objectId$$]).pipe(
    switchMap(([objectType, objectId]) => this.getPrimaryAssociation(objectType, objectId)),
  );

  protected readonly displayName$ = this.primaryAssociation$.pipe(
    map((primaryAssociation) => {
      if (primaryAssociation?.crmObject) {
        return this.objectDisplayService.getObjectDisplayName(
          primaryAssociation.objectType,
          primaryAssociation.crmObject,
        );
      }
      return '';
    }),
  );
  protected readonly redirectUrl$ = combineLatest([
    this.primaryAssociation$,
    this.config.routePrefix$,
    this.showUrl$$,
  ]).pipe(
    map(([primaryAssociation, pathRoot, showUrl]) => {
      if (!primaryAssociation?.crmObject || !pathRoot || !showUrl) {
        return '';
      }
      let pathTemplate = '';
      switch (primaryAssociation?.objectType) {
        case 'Company':
          pathTemplate = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
          break;
        case 'Contact':
          pathTemplate = `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
          break;
        default:
          return '';
      }
      const objectId = primaryAssociation?.crmObject?.crmObjectId ?? '';
      return `${pathRoot}/${pathTemplate}`.replace(':crmObjectId', objectId);
    }),
  );

  constructor(
    private readonly crmAssociationService: CrmAssociationService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
  ) {
    super();
  }

  private getPrimaryAssociation(objectType: ObjectType, objectId: string): Observable<primaryAssociation | undefined> {
    if (!objectId || !objectType) {
      return of(undefined);
    }
    const contact$ = this.crmAssociationService
      .getAssociationsWithAssociatedObjectForObject$(objectId, objectType, 'Contact', 1)
      .pipe(
        map((result) => {
          const association = result?.find((r) => Boolean(r.associatedObject));
          return {
            crmObject: association?.associatedObject,
            objectType: 'Contact',
          } as primaryAssociation;
        }),
      );
    const company$ = this.crmAssociationService
      .getAssociationsWithAssociatedObjectForObject$(objectId, objectType, 'Company', 1)
      .pipe(
        map((result) => {
          const association = result?.find((r) => Boolean(r.associatedObject));
          return {
            crmObject: association?.associatedObject,
            objectType: 'Company',
          } as primaryAssociation;
        }),
      );
    return contact$.pipe(switchWhen(company$, (value) => value.crmObject === undefined));
  }
}
